"use client"
import { motion } from 'framer-motion';
import { CitationVerificationVisual, DataAggregationVisual } from './feature-elements/DeepResearchVisuals';
import Link from "next/link";
import Image from "next/image";
// Reusing Icons/graphics from the request description


// Reusing Icons/graphics from the request description

const DeepResearch = () => {
    return (
        <div className="w-full bg-white text-black font-jakarta">

            {/* Hero Section */}
            <section className="relative pt-32 pb-20 overflow-hidden bg-white">
                {/* Background Grid & Decorative Elements */}
                <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px] pointer-events-none" />
                <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-50/50 rounded-full blur-3xl -z-10 translate-x-1/3 -translate-y-1/4" />

                {/* Dotted Lines Decorative */}
                <svg className="absolute w-full h-full inset-0 pointer-events-none opacity-20" aria-hidden="true">
                    <defs>
                        <pattern id="grid-pattern" width="40" height="40" patternUnits="userSpaceOnUse">
                            <circle cx="2" cy="2" r="1" fill="currentColor" className="text-gray-400" />
                        </pattern>
                    </defs>
                    <rect width="100%" height="100%" fill="url(#grid-pattern)" />
                </svg>

                <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
                    <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">

                        {/* Left: Content */}
                        <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.6 }}
                            className="max-w-2xl"
                        >

                            <h1 className="font-syne font-bold text-[46px] sm:text-[56px] lg:text-[46px] text-gray-900 leading-[1.1] mb-6 tracking-tight">
                                Deep Research <span className="relative inline-block text-[#2563EB]">
                                    Made Simple
                                    <svg className="absolute w-full h-3 -bottom-1 left-0 text-blue-200 -z-10 opacity-60" viewBox="0 0 100 10" preserveAspectRatio="none">
                                        <motion.path
                                            d="M0 5 Q 50 10 100 5"
                                            stroke="currentColor"
                                            strokeWidth="12"
                                            fill="none"
                                            initial={{ pathLength: 0 }}
                                            animate={{ pathLength: 1 }}
                                            transition={{ duration: 0.8, delay: 0.5 }}
                                        />
                                    </svg>
                                </span>
                            </h1>

                            <p className="font-jakarta text-lg text-gray-500 leading-relaxed mb-8 max-w-lg">
                                Stop spending hours scouring the web. Our AI digs deep into reliable sources to find data, market trends, and competitive insights instantly.
                            </p>

                            <div className="flex flex-wrap items-center gap-6">
                                <div className="relative group">
                                    {/* Main CTA */}
                                    <Link href="/signup" className="relative px-6 py-2.5 bg-[#2563EB] text-white rounded-full font-semibold text-sm shadow-sm hover:shadow-md hover:bg-blue-700 transition-all overflow-hidden inline-block">
                                        Start Researching
                                    </Link>

                                    {/* Scribble Arrow */}
                                    <div className="absolute left-[105%] top-[-50%] w-40 hidden md:block pointer-events-none">
                                        <svg viewBox="0 0 200 100" fill="none" className="w-full h-full text-gray-800/80">
                                            <path
                                                d="M160 30 C 140 10, 60 10, 30 40"
                                                stroke="currentColor"
                                                strokeWidth="1.5"
                                                strokeLinecap="round"
                                                className="opacity-90"
                                            />
                                            <path
                                                d="M25 32 L 30 40 L 38 38"
                                                stroke="currentColor"
                                                strokeWidth="1.5"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                className="opacity-90"
                                            />
                                            <text
                                                x="50"
                                                y="70"
                                                fontSize="14"
                                                fill="currentColor"
                                                className="font-handwriting font-medium opacity-90"
                                                style={{ fontFamily: 'cursive' }}
                                            >
                                                *Free trial
                                            </text>
                                        </svg>
                                    </div>
                                </div>


                            </div>
                        </motion.div>

                        {/* Right: Image/Visual */}
                        <motion.div
                            initial={{ opacity: 0, x: 20, scale: 0.95 }}
                            animate={{ opacity: 1, x: 0, scale: 1 }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                            className="relative"
                        >
                            {/* Removed Floating Icons */}

                            <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-gray-200 bg-white aspect-[16/10]">
                                <Image
                                    src="https://seedicon-dev-public-images.s3.ap-south-1.amazonaws.com/1769274729426_6jl48o2n165_Deep Research.png"
                                    alt="Deep Research Interface"
                                    fill
                                    className="object-fill"
                                    priority
                                    sizes="(max-width: 768px) 100vw, 50vw"
                                />
                                {/* Gradient Overlay */}
                                <div className="absolute inset-0 bg-gradient-to-tr from-blue-500/10 to-transparent pointer-events-none" />
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Explanation Section */}
            <section className="py-24 bg-[#ffffff]">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="text-center mb-16 max-w-3xl mx-auto">
                        <h2 className="font-syne font-bold text-3xl md:text-4xl text-gray-900 mb-4">
                            What is Deep Research?
                        </h2>
                        <p className="text-gray-500 text-lg">
                            An intelligent research assistant that aggregates, analyzes, and cites information from millions of trusted sources in seconds.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-12 items-center mb-24">
                        <div className="order-2 md:order-1">
                            <div className="bg-white p-2 rounded-2xl border border-gray-100 shadow-xl rotate-1 hover:rotate-0 transition-transform duration-500">
                                <DataAggregationVisual />
                            </div>
                        </div>
                        <div className="order-1 md:order-2">
                            {/* Removed Icon above heading */}
                            <h3 className="font-syne font-bold text-2xl md:text-3xl text-gray-900 mb-4">
                                Instant Data Aggregation
                            </h3>
                            <p className="text-gray-500 text-lg leading-relaxed mb-6">
                                Deckwale doesn't just guess. It scans up-to-date reports, news, and databases to bring you factual data points relevant to your topic.
                            </p>
                            <ul className="space-y-3">
                                {[
                                    "Real-time market data access",
                                    "Competitor analysis summaries",
                                    "Trend forecasting with credible sources"
                                ].map((item, i) => (
                                    <li key={i} className="flex items-center gap-2 text-gray-700 font-medium">
                                        <div className="w-1.5 h-1.5 rounded-full bg-blue-600" />
                                        {item}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-12 items-center">
                        <div>
                            {/* Removed Icon above heading */}
                            <h3 className="font-syne font-bold text-2xl md:text-3xl text-gray-900 mb-4">
                                Auto-Citation & Verification
                            </h3>
                            <p className="text-gray-500 text-lg leading-relaxed mb-6">
                                Every data point comes with a source. Build credibility with investors by showing exactly where your numbers come from.
                            </p>
                            <ul className="space-y-3">
                                {[
                                    "Automatic bibliography generation",
                                    "Cross-referenced facts for accuracy",
                                    "One-click drill down into source material"
                                ].map((item, i) => (
                                    <li key={i} className="flex items-center gap-2 text-gray-700 font-medium">
                                        <div className="w-1.5 h-1.5 rounded-full bg-indigo-600" />
                                        {item}
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div>
                            <div className="bg-white p-2 rounded-2xl border border-gray-100 shadow-xl -rotate-1 hover:rotate-0 transition-transform duration-500">
                                <CitationVerificationVisual />
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default DeepResearch;
